/**
 * Client-side destination search backed by the Liquid-generated collection index.
 *
 * Empty input renders curated discovery menus; a typed query uses the same index
 * with country-aware ranking and coverage-based regional expansion.
 */
class DestinationSearch extends HTMLElement {
  connectedCallback() {
    // A page-owned search can move between its original position and sticky header.
    // Keep the existing state and listeners when the same element reconnects.
    if (this.initialized) return;
    this.initialized = true;

    this.input = this.querySelector('.destination-search__input');
    this.form = this.querySelector('form');
    this.results = this.querySelector('[data-results]');
    this.clearButton = this.querySelector('.destination-search__clear');
    this.index = this.readIndex();
    this.indexByHandle = new Map(this.index.map((destination) => [destination.handle, destination]));
    this.discovery = this.readDiscovery();
    this.regionalMenuRanks = new Map(
      this.discovery.regionalMenuOrder.map((handle, index) => [handle, index])
    );
    this.items = [];
    this.activeIndex = -1;

    this.input.addEventListener('input', () => {
      this.update();
    });
    this.input.addEventListener('focus', () => this.update());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.clearButton.addEventListener('click', () => this.clear());
    this.form.addEventListener('submit', (event) => event.preventDefault());
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
    this.addEventListener('focusout', () => this.onFocusOut());
    this.updateClearButton();
  }

  onFocusOut() {
    // Defer dismissal so iOS Safari can finish the tap/click sequence for a
    // destination link after the search input releases focus.
    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  readIndex() {
    const source = this.querySelector('[data-destination-index]');
    if (!source) return [];

    try {
      const destinations = JSON.parse(source.textContent);
      const expectedCount = Number(this.dataset.destinationCount);
      const hasValidDestinationShape = destinations.every((destination) => (
        destination &&
        typeof destination.id === 'string' &&
        typeof destination.handle === 'string' &&
        typeof destination.name === 'string' &&
        typeof destination.url === 'string' &&
        typeof destination.type === 'string' &&
        typeof destination.countryHandle === 'string' &&
        (destination.countryId === null || typeof destination.countryId === 'string') &&
        Array.isArray(destination.coverageCountryIds) &&
        Array.isArray(destination.coverageCountries)
      ));

      // Do not offer an incomplete search experience when Shopify rendered an
      // empty or malformed index. The expected count catches truncated JSON too.
      if (!Array.isArray(destinations) || !destinations.length || destinations.length !== expectedCount || !hasValidDestinationShape) {
        throw new Error('Destination index is empty or does not match the generated destination data.');
      }

      return destinations;
    } catch (error) {
      console.error('Destination search index could not be initialized.', error);
      return [];
    }
  }

  readDiscovery() {
    const source = this.querySelector('[data-destination-discovery]');
    if (!source) return { popularMenuOrder: [], regionalMenuOrder: [] };

    try {
      const discovery = JSON.parse(source.textContent);
      const popularMenuOrder = Array.isArray(discovery.popularMenuOrder) ? discovery.popularMenuOrder : [];
      const regionalMenuOrder = Array.isArray(discovery.regionalMenuOrder) ? discovery.regionalMenuOrder : [];

      return {
        popularMenuOrder: popularMenuOrder.filter((handle) => typeof handle === 'string'),
        regionalMenuOrder: regionalMenuOrder.filter((handle) => typeof handle === 'string'),
      };
    } catch (error) {
      console.error('Destination discovery menus could not be initialized.', error);
      return { popularMenuOrder: [], regionalMenuOrder: [] };
    }
  }

  normalize(value) {
    return window.DestinationSearchEngine.normalize(value);
  }

  score(destination, query) {
    return window.DestinationSearchEngine.score(destination, query);
  }

  findMatches(query) {
    const directMatches = window.DestinationSearchEngine.directMatches(this.index, query);

    // An exact country code is authoritative: return that country and only the
    // regional destinations whose coverage includes it.
    const exactIso2 = directMatches.find((match) => match.destination.type === 'LOCAL' && match.score === 1000);
    if (exactIso2) return this.withRelatedDestinations([exactIso2.destination], exactIso2.destination);

    const exactIso3 = directMatches.find((match) => match.destination.type === 'LOCAL' && match.score === 990);
    if (exactIso3) {
      const strongPrefixMatches = directMatches
        .filter((match) => match.destination.id !== exactIso3.destination.id && match.score >= 830)
        .map((match) => match.destination);
      return this.withRelatedDestinations([exactIso3.destination, ...strongPrefixMatches], exactIso3.destination);
    }

    const exactCountryName = directMatches.find((match) => (
      match.destination.type === 'LOCAL' && match.score >= 960
    ));
    if (exactCountryName) {
      return this.withRelatedDestinations([exactCountryName.destination], exactCountryName.destination);
    }

    // Prefixes are useful from two characters onwards. If a local country leads,
    // append its covered regions before falling back to weak substring matches.
    const prefixMatches = directMatches.filter((match) => match.score >= 830);
    const strongestLocalPrefix = query.length >= 2
      ? prefixMatches.find((match) => match.destination.type === 'LOCAL')
      : null;
    if (strongestLocalPrefix) {
      return this.withRelatedDestinations(
        prefixMatches.map((match) => match.destination),
        strongestLocalPrefix.destination
      );
    }

    const substringMatches = query.length >= 3
      ? directMatches.filter((match) => match.score === 700)
      : [];

    return [...prefixMatches, ...substringMatches]
      .map((match) => match.destination);
  }

  relatedDestinations(localDestination) {
    return this.index.filter((destination) => {
      const isRegional = destination.type === 'REGIONAL';
      if (!isRegional) return false;

      // Coverage metafields reference country metaobjects. Prefer their stable
      // handles, while also accepting IDs so existing Shopify data still links
      // correctly if either representation is present in the generated index.
      const matchesHandle = destination.coverageCountries.includes(localDestination.countryHandle);
      const matchesId = Boolean(
        localDestination.countryId && destination.coverageCountryIds.includes(localDestination.countryId)
      );
      return matchesHandle || matchesId;
    }).sort((left, right) => {
      const leftRank = this.regionalMenuRanks.get(left.handle) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = this.regionalMenuRanks.get(right.handle) ?? Number.MAX_SAFE_INTEGER;
      return leftRank - rightRank || left.name.localeCompare(right.name);
    });
  }

  withRelatedDestinations(directDestinations, localDestination) {
    const directIds = new Set(directDestinations.map((destination) => destination.id));
    const related = this.relatedDestinations(localDestination)
      .filter((destination) => !directIds.has(destination.id));

    return [...directDestinations, ...related];
  }

  limitMatches(matches, query) {
    // Apply the cap only after coverage expansion. A strong country match must
    // retain its related regional options ahead of weak direct matches.
    const limit = query.length === 1 ? 5 : 6;
    if (matches.length <= limit) return matches;

    const strongLocal = this.strongLocalForQuery(matches, query);
    if (!strongLocal) return matches.slice(0, limit);

    const relatedIds = new Set(
      this.relatedDestinations(strongLocal).map((destination) => destination.id)
    );
    const related = matches.filter((destination) => relatedIds.has(destination.id));
    const direct = matches.filter((destination) => (
      destination.id !== strongLocal.id && !relatedIds.has(destination.id)
    ));
    const relatedLimit = Math.min(related.length, limit - 1);
    const directLimit = limit - 1 - relatedLimit;

    return [
      strongLocal,
      ...direct.slice(0, directLimit),
      ...related.slice(0, relatedLimit),
    ];
  }

  strongLocalForQuery(destinations, query) {
    const threshold = query.length >= 2 ? 830 : 960;
    return destinations.find((destination) => (
      destination.type === 'LOCAL' && this.score(destination, query) >= threshold
    ));
  }

  update() {
    this.updateClearButton();
    if (!this.index.length) return this.close();

    const query = this.normalize(this.input.value);
    if (!query) {
      this.renderEmptyState();
      return;
    }

    const matches = this.limitMatches(this.findMatches(query), query);
    if (matches.length) {
      this.renderSearchResults(matches);
    } else {
      this.renderNoResults(this.input.value.trim());
    }
  }

  renderEmptyState() {
    // Discovery is intentionally separate from typed-result rendering: curated
    // entries show only an icon and name, never prices or destination-type pills.
    const popular = this.destinationsForMenu(this.discovery.popularMenuOrder);
    const regional = this.destinationsForMenu(this.discovery.regionalMenuOrder);

    this.results.replaceChildren();
    const discovery = document.createElement('div');
    discovery.className = 'destination-search__discovery';
    this.renderDiscoveryGroup(discovery, this.dataset.popularHeading, popular, true);
    this.renderDiscoveryGroup(discovery, this.dataset.regionalHeading, regional, false);
    this.results.append(discovery);
    this.open();
  }

  destinationsForMenu(menuOrder) {
    // Mapping the configured handles preserves the merchant's metafield order.
    return menuOrder
      .map((handle) => this.indexByHandle.get(handle))
      .filter(Boolean);
  }

  renderDiscoveryGroup(container, heading, destinations, isPopular) {
    const group = document.createElement('div');
    group.className = 'destination-search__discovery-group';
    const title = document.createElement('p');
    title.className = 'destination-search__group-title';
    title.textContent = heading;
    title.id = `${this.input.id}-group-${container.children.length}`;
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', title.id);
    group.append(title);

    const list = document.createElement('div');
    list.className = `destination-search__discovery-list${isPopular ? ' destination-search__discovery-list--popular' : ''}`;
    destinations.forEach((destination) => list.append(this.renderDiscoveryItem(destination)));
    group.append(list);
    container.append(group);
  }

  renderDiscoveryItem(destination) {
    const option = this.createOption(destination, 'destination-search__discovery-item');
    const name = document.createElement('span');
    name.className = 'destination-search__result-name';
    name.textContent = destination.name;
    const imageUrl = destination.type === 'LOCAL' ? destination.flagUrl : destination.collectionImageUrl;
    option.append(this.renderDestinationIcon(destination, imageUrl), name);
    return option;
  }

  renderSearchResults(destinations) {
    this.results.replaceChildren();
    const list = document.createElement('div');
    list.className = 'destination-search__list';
    const query = this.normalize(this.input.value);
    const strongLocal = this.strongLocalForQuery(destinations, query);
    const relatedIds = new Set(
      strongLocal ? this.relatedDestinations(strongLocal).map((destination) => destination.id) : []
    );
    let relatedHeadingRendered = false;

    destinations.forEach((destination) => {
      // Label the first coverage-derived result without changing result order.
      if (!relatedHeadingRendered && relatedIds.has(destination.id)) {
        const heading = document.createElement('p');
        heading.className = 'destination-search__group-title destination-search__related-heading';
        heading.textContent = this.dataset.relatedHeading;
        list.append(heading);
        relatedHeadingRendered = true;
      }
      list.append(this.renderSearchResult(destination));
    });
    this.results.append(list);
    this.open();
  }

  renderSearchResult(destination) {
    const option = this.createOption(destination, 'destination-search__result');
    const imageUrl = destination.type === 'LOCAL' ? destination.flagUrl : destination.collectionImageUrl;
    const icon = this.renderDestinationIcon(destination, imageUrl);

    const title = document.createElement('span');
    title.className = 'destination-search__result-name';
    title.textContent = destination.name;

    const type = document.createElement('span');
    type.className = 'destination-search__result-type';
    type.textContent = this.destinationType(destination.type);

    const details = document.createElement('span');
    details.className = 'destination-search__result-details';
    const content = document.createElement('span');
    content.className = 'destination-search__result-content';
    content.append(title);
    if (destination.fixedFromUsdCents || destination.unlimitedFromUsdCents) {
      this.appendPrice(details, this.dataset.fixedFromLabel, destination.fixedFromUsdCents);
      this.appendPrice(details, this.dataset.unlimitedFromLabel, destination.unlimitedFromUsdCents);
      content.append(details);
    }

    option.append(icon, content, type);
    return option;
  }

  appendPrice(container, label, usdCents) {
    if (!usdCents) return;
    const row = document.createElement('span');
    row.className = 'destination-search__result-price';
    row.append(document.createTextNode(`${label} `));
    const price = document.createElement('span');
    price.dataset.priceUsdCents = String(usdCents);
    price.textContent = window.displayCurrency?.formatCurrency(usdCents) || '';
    row.append(price);
    container.append(row);
  }

  createOption(destination, className) {
    const option = document.createElement('a');
    option.className = className;
    option.href = destination.url;
    option.id = `${this.input.id}-option-${destination.id}`;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    return option;
  }

  renderDestinationIcon(destination, imageUrl = destination.flagUrl) {
    const icon = document.createElement('span');
    icon.className = 'destination-search__result-icon';
    if (destination.type === 'LOCAL') icon.classList.add('destination-search__result-icon--local');
    icon.setAttribute('aria-hidden', 'true');
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      icon.append(image);
    } else {
      // Text is only a last-resort fallback when neither a country flag nor a
      // collection image has been configured in Shopify.
      icon.textContent = destination.iso2 || destination.name.slice(0, 2);
    }
    return icon;
  }

  renderNoResults(query) {
    this.results.replaceChildren();
    const message = document.createElement('p');
    message.className = 'destination-search__empty';
    message.textContent = this.dataset.noResultsTemplate.replace('[query]', query);
    this.results.append(message);
    this.open();
  }

  destinationType(type) {
    if (type === 'REGIONAL') return this.dataset.regionalType;
    return this.dataset.localType;
  }

  open() {
    this.items = [...this.results.querySelectorAll('[role="option"]')];
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.results.hidden = !this.results.childElementCount;
    this.input.setAttribute('aria-expanded', String(!this.results.hidden));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.blur();
      return;
    }

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !this.items.length) this.update();
    if (!this.items.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.activeIndex + offset + this.items.length) % this.items.length);
    }

    if (event.key === 'Enter' && this.activeIndex > -1) {
      event.preventDefault();
      window.location.assign(this.items[this.activeIndex].href);
    }
  }

  setActive(index) {
    this.activeIndex = index;
    this.items.forEach((item, itemIndex) => {
      item.setAttribute('aria-selected', String(itemIndex === index));
    });
    this.input.setAttribute('aria-activedescendant', this.items[index].id);
    this.items[index].scrollIntoView({ block: 'nearest' });
  }

  clear() {
    this.input.value = '';
    this.input.focus();
    this.update();
  }

  updateClearButton() {
    // The custom control replaces the native search clear affordance and should
    // never appear until there is meaningful text to clear.
    this.clearButton.hidden = this.input.value.trim().length === 0;
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.items = [];
    this.activeIndex = -1;
  }
}

if (!customElements.get('destination-search')) {
  customElements.define('destination-search', DestinationSearch);
}
